/**
 * LandingFooter - Navy background with gold accents
 * Content aligned with actual landing page sections
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Typography, Box, Container, IconButton, Stack, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';

import { COLORS, GRADIENTS, TRANSITIONS, RADIUS, TYPOGRAPHY } from './landingTheme';

// NARA Logo
const LOGO_PATH = '/images/logo-nara-png.png';

// Scroll helper
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

// Footer links — real content only
const FOOTER_SECTIONS = [
  {
    title: 'Producto',
    links: [
      { label: 'Funcionalidades', scroll: 'funcionalidades' },
      { label: 'Plataforma', scroll: 'plataforma' },
      { label: 'Planes', scroll: 'precios' },
      { label: 'Tecnología', scroll: 'tecnologia' },
    ],
  },
  {
    title: 'Soluciones',
    links: [
      { label: 'Medicina General', scroll: 'soluciones' },
      { label: 'Dermatología', scroll: 'soluciones' },
      { label: 'Pediatría', scroll: 'soluciones' },
      { label: 'Odontología', scroll: 'soluciones' },
    ],
  },
  {
    title: 'Soporte',
    links: [
      { label: 'Cómo empezar', scroll: 'como-empezar' },
      { label: 'Preguntas frecuentes', scroll: 'faq' },
      { label: 'soporte@nara.health', href: 'mailto:soporte@nara.health' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Política de privacidad', scroll: 'faq' },
      { label: 'Términos de servicio', scroll: 'faq' },
      { label: 'HIPAA Compliance', scroll: 'faq' },
    ],
  },
];

// Footer column component
const FooterColumn = ({ section }) => (
  <Box>
    <Typography
      sx={{
        fontWeight: 700,
        color: '#f8fafc',
        fontSize: TYPOGRAPHY.tiny,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mb: 2,
      }}
    >
      {section.title}
    </Typography>
    <Stack spacing={1.2}>
      {section.links.map((link, idx) => (
        <Typography
          key={idx}
          component={link.href ? (link.href.startsWith('mailto:') ? 'a' : RouterLink) : 'span'}
          {...(link.href
            ? link.href.startsWith('mailto:')
              ? { href: link.href }
              : { to: link.href }
            : {})}
          onClick={link.scroll ? () => scrollTo(link.scroll) : undefined}
          sx={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: TYPOGRAPHY.small,
            textDecoration: 'none',
            transition: TRANSITIONS.fast,
            cursor: 'pointer',
            '&:hover': { color: COLORS.accent },
          }}
        >
          {link.label}
        </Typography>
      ))}
    </Stack>
  </Box>
);

export default function LandingFooter() {
  return (
    <Box component="footer">
      {/* CTA Section — navy gradient */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #0f2440 0%, #1e3a5f 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.06) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="sm" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: TYPOGRAPHY.h2,
              fontWeight: 800,
              color: '#fff',
              mb: 1.5,
              letterSpacing: '-0.02em',
            }}
          >
            ¿Listo para automatizar tu consultorio?
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: TYPOGRAPHY.body,
              mb: 3.5,
              maxWidth: 420,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Prueba NARA gratis por 15 días y descubre cómo puede transformar
            la atención de tus pacientes.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                background: GRADIENTS.primaryButton,
                color: '#0f2440',
                fontWeight: 700,
                px: 3.5,
                py: 1.3,
                fontSize: '0.95rem',
                borderRadius: RADIUS.md,
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.35)',
                textTransform: 'none',
                '&:hover': {
                  background: GRADIENTS.primaryButtonHover,
                  boxShadow: '0 8px 30px rgba(245, 158, 11, 0.45)',
                },
              }}
            >
              Comenzar gratis
            </Button>
            <Button
              component="a"
              href="https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio"
              target="_blank"
              rel="noopener"
              startIcon={<WhatsAppIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                color: 'rgba(255,255,255,0.8)',
                borderColor: 'rgba(255,255,255,0.2)',
                fontWeight: 500,
                px: 3,
                py: 1.2,
                fontSize: '0.9rem',
                borderRadius: RADIUS.md,
                textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  background: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              Solicitar demo
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Main footer — dark navy */}
      <Box sx={{ background: '#0f172a' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: '2.5fr 1fr 1fr 1fr 1fr' },
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* Brand column */}
            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  component="img"
                  src={LOGO_PATH}
                  alt="NARA"
                  sx={{ height: 28, width: 'auto' }}
                />
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f8fafc' }}>
                  NARA
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: TYPOGRAPHY.small,
                  lineHeight: 1.7,
                  maxWidth: 280,
                  mb: 2.5,
                }}
              >
                Asistente médico con inteligencia artificial en WhatsApp.
                Diagnóstico, citas y memoria del paciente — 24/7.
              </Typography>

              {/* Contact info */}
              <Stack spacing={1} sx={{ mb: 2.5 }}>
                <Box
                  component="a"
                  href="mailto:soporte@nara.health"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                    color: 'rgba(255,255,255,0.4)',
                    textDecoration: 'none',
                    fontSize: TYPOGRAPHY.small,
                    transition: TRANSITIONS.fast,
                    '&:hover': { color: COLORS.accent },
                  }}
                >
                  <EmailOutlinedIcon sx={{ fontSize: 15 }} />
                  soporte@nara.health
                </Box>
              </Stack>

              {/* Social media */}
              <Stack direction="row" spacing={1}>
                {[
                  { icon: <InstagramIcon />, href: 'https://instagram.com/nara.health', label: 'Instagram' },
                  { icon: <FacebookIcon />, href: 'https://facebook.com/nara.health', label: 'Facebook' },
                  { icon: <XIcon />, href: 'https://x.com/narahealth', label: 'X' },
                  { icon: <YouTubeIcon />, href: 'https://youtube.com/@narahealth', label: 'YouTube' },
                ].map((social) => (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener"
                    aria-label={social.label}
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.35)',
                      transition: TRANSITIONS.fast,
                      '&:hover': { color: COLORS.accent, background: 'rgba(245, 158, 11, 0.1)' },
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Stack>
            </Box>

            {/* Link columns */}
            {FOOTER_SECTIONS.map((section, idx) => (
              <FooterColumn key={idx} section={section} />
            ))}
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              mt: 5,
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: TYPOGRAPHY.tiny }}>
              © 2026 NARA Technology. Todos los derechos reservados.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: TYPOGRAPHY.tiny,
                }}
              >
                HIPAA Compliant
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: TYPOGRAPHY.tiny,
                }}
              >
                GDPR Ready
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
